import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import './CheckoutPages.css';
import ConfirmationModal from './components/ConfirmationModal';
import { useConfirmationModal } from './hooks/useConfirmationModal';
import api from './utils/api';

const CancelSession = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refundInfo, setRefundInfo] = useState(null);
  const confirmationModal = useConfirmationModal();

  useEffect(() => {
    validateCancellationToken();
  }, [token]);

  const validateCancellationToken = async () => {
    try {
      const response = await api.post('/api/validateCancellationToken', { token });

      if (!response.ok) {
        throw new Error('Invalid or expired cancellation link');
      }

      const data = await response.json();
      setSessionDetails(data.session);
    } catch (err) {
      setError(err.message || 'Failed to validate cancellation link');
    } finally {
      setLoading(false);
    }
  };  const handleCancelSession = async () => {
    const refundMessage = (() => {
      if (sessionDetails?.eligibleForRefund) {
        if (sessionDetails.requestedByUserType === 'tutor') {
          return 'If payment has been processed, a refund will be issued to your original payment method within 5-10 business days (tutor cancellation policy).';
        } else {
          return 'If payment has been processed, a refund will be issued to your original payment method within 5-10 business days.';
        }
      } else {
        return 'Since this session is being cancelled within 24 hours of the start time, no refund will be issued if payment has been processed (student cancellation policy).';
      }
    })();
    
    // Show confirmation modal instead of window.confirm
    confirmationModal.showConfirmation({
      title: 'Cancel Session',
      message: `Are you sure you want to cancel this session? ${refundMessage}`,
      confirmText: 'Cancel Session',
      cancelText: 'Keep Session',
      type: 'danger',
      onConfirm: async () => {
        await performCancellation();
      }
    });
  };

  const performCancellation = async () => {
    setCancelling(true);
    try {
      const response = await api.post('/api/cancelSession', { token });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific race condition errors
        if (errorData.errorCode) {
          switch (errorData.errorCode) {
            case 'PAYMENT_IN_PROGRESS':
              throw new Error('Cannot cancel while payment is being processed. Please wait a moment and try again.');
            case 'CONFIRMATION_IN_PROGRESS':
              throw new Error('Cannot cancel while confirmation is in progress. Please wait a moment and try again.');
            default:
              throw new Error(errorData.message || 'Failed to cancel session');
          }
        } else {
          throw new Error(errorData.message || 'Failed to cancel session');
        }
      }

      const data = await response.json();
      setRefundInfo(data.refund);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to cancel session');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateValue) => {
    try {
      let date = dateValue;
      if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        date = new Date(dateValue);
      }
      
      if (date && !isNaN(date)) {
        const monthsOfYear = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return `${monthsOfYear[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      }
      return 'Unknown date';
    } catch (error) {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="checkout-page">
          <div className="checkout-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h2>Validating Cancellation Request...</h2>
              <p>Please wait while we verify your cancellation link.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="checkout-page">
          <div className="checkout-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h2>Cancellation Error</h2>
              <div className="payment-error" style={{ marginBottom: '2rem' }}>
                {error}
              </div>
              <p>If you believe this is an error, please contact us at <a href="mailto:bright.bridge.tutors@gmail.com">bright.bridge.tutors@gmail.com</a> with your booking ID.</p>
              <button 
                className="btn-primary"
                onClick={() => navigate('/dashboard')}
                style={{ marginTop: '1rem' }}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <Header />
        <div className="checkout-page">
          <div className="checkout-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                margin: '0 auto 20px', 
                background: '#dc3545', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '40px', color: 'white' }}>✖</span>
              </div>
              
              <h2>Session Cancelled Successfully</h2>
              
              <div className="payment-success" style={{ marginBottom: '2rem' }}>
                Your tutoring session has been cancelled and all participants have been notified.
              </div>

              {refundInfo && (
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '8px', 
                  padding: '1rem', 
                  marginBottom: '2rem',
                  textAlign: 'left'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Refund Information</h4>
                  <p style={{ margin: '0', color: '#856404' }}>
                    <strong>Amount:</strong> ${refundInfo.amount.toFixed(2)}<br />
                    <strong>Method:</strong> Original payment method<br />
                    <strong>Timeline:</strong> 5-10 business days<br />
                    <strong>Refund ID:</strong> {refundInfo.refundId}
                  </p>
                </div>
              )}

              <p>You will receive a confirmation email shortly. If you have any questions, please contact us at <a href="mailto:bright.bridge.tutors@gmail.com">bright.bridge.tutors@gmail.com</a></p>
              
              <button 
                className="btn-primary"
                onClick={() => navigate('/dashboard')}
                style={{ marginTop: '1rem' }}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="checkout-page">
        <div className="checkout-content">
          <h1>Cancel Tutoring Session</h1>
          
          {sessionDetails && (
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '1.5rem', 
              marginBottom: '2rem',
              backgroundColor: '#f8f9fa'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#333' }}>Session Details</h3>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Date:</strong> {formatDate(sessionDetails.date || sessionDetails.bookingDate)}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Time:</strong> {sessionDetails.startTime} - {sessionDetails.endTime}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Tutor:</strong> {sessionDetails.tutorName} ({sessionDetails.tutorEmail})
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Student:</strong> {sessionDetails.studentName} ({sessionDetails.studentEmail})
              </div>
              {sessionDetails.description && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Details:</strong> {sessionDetails.description}
                </div>
              )}              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Status:</strong> {sessionDetails.type === 'confirmed' ? 'Confirmed & Paid' : 'Reserved'}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Booking ID:</strong> {sessionDetails.id}
              </div>              <div style={{ 
                marginTop: '1rem',
                padding: '0.75rem',
                borderRadius: '6px',
                backgroundColor: sessionDetails.eligibleForRefund ? '#d1e7dd' : '#f8d7da',
                border: `1px solid ${sessionDetails.eligibleForRefund ? '#badbcc' : '#f5c6cb'}`
              }}>
                <strong style={{ color: sessionDetails.eligibleForRefund ? '#0f5132' : '#721c24' }}>
                  Refund Status: 
                </strong>
                <span style={{ color: sessionDetails.eligibleForRefund ? '#0f5132' : '#721c24' }}>
                  {(() => {
                    if (sessionDetails.eligibleForRefund) {
                      if (sessionDetails.requestedByUserType === 'tutor') {
                        return ' ✓ Eligible for full refund (tutor cancellation policy)';
                      } else {
                        return ' ✓ Eligible for full refund (24+ hours advance notice)';
                      }
                    } else {
                      return ' ✗ Not eligible for refund (student cancellation within 24 hours)';
                    }
                  })()}
                </span>
              </div>
            </div>
          )}

          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '2rem'          }}>            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Cancellation Policy</h4>
            <ul style={{ margin: '0', paddingLeft: '1.2rem', color: '#856404' }}>
              <li>Sessions can be cancelled until the start time</li>
              <li><strong>Tutors:</strong> Always eligible for full refunds when cancelling sessions</li>
              <li><strong>Students:</strong> Full refunds available when cancelled 24+ hours in advance</li>
              <li><strong>Students:</strong> No refunds for cancellations within 24 hours of session start</li>
              <li>Refunds are processed to your original payment method within 5-10 business days</li>
              <li>Both the tutor and student will be notified of the cancellation</li>
              <li>The Google Meet link will be automatically deactivated</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button 
              className="btn-outline"
              onClick={() => navigate('/dashboard')}
              disabled={cancelling}
            >
              Keep Session
            </button>
            <button 
              className="btn-primary"
              onClick={handleCancelSession}
              disabled={cancelling}
              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >              {cancelling ? 'Cancelling...' : 'Cancel Session'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isVisible={confirmationModal.isVisible}
        title={confirmationModal.config.title}
        message={confirmationModal.config.message}
        confirmText={confirmationModal.config.confirmText}
        cancelText={confirmationModal.config.cancelText}
        type={confirmationModal.config.type}
        onConfirm={confirmationModal.handleConfirm}
        onCancel={confirmationModal.handleCancel}
      />
    </>
  );
};

export default CancelSession;
